import InfoIcon from "@mui/icons-material/Info";
import {
  Tooltip,
  styled,
  tooltipClasses,
  Paper,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

// Estilizando o tooltip para ter aparência customizada
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 400,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
}));

// Mapeamento dos tipos de motivo para exibição legível
const MOTIVOS_NAO_EXECUCAO_LEGIVEL = {
  CRIANCA_NAO_COLABOROU: "Criança não colaborou",
  PACIENTE_NAO_COLABOROU: "Paciente não colaborou",
  CONDICAO_CLINICA_INADEQUADA: "Condição clínica inadequada",
  EQUIPAMENTO_INDISPONIVEL: "Equipamento indisponível",
  OUTRO: "Outro motivo",
};

// Cores para os chips de motivo
const MOTIVO_COLORS = {
  CRIANCA_NAO_COLABOROU: "#ffb74d", // laranja
  PACIENTE_NAO_COLABOROU: "#ff8a65", // coral
  CONDICAO_CLINICA_INADEQUADA: "#ef5350", // vermelho
  EQUIPAMENTO_INDISPONIVEL: "#90caf9", // azul claro
  OUTRO: "#e0e0e0", // cinza claro
};

const NaoExecucaoInfoTooltip = ({ estatisticasNaoExecucao }) => {
  // Formatação da data para exibição
  const formatarData = (dataString) => {
    try {
      const data = new Date(dataString);
      return format(data, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (e) {
      return "Data inválida";
    }
  };

  return (
    <span className="naoExecucaoInfoTooltip">
      <span>{estatisticasNaoExecucao.totalNaoExecutados}</span>
      <HtmlTooltip
        title={
          <Paper elevation={0} sx={{ p: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Estatísticas de Não Execução
            </Typography>

            {/* Resumo geral */}
            <div style={{ marginBottom: "12px" }}>
              <Typography variant="body2">
                <strong>Total de não executados:</strong>{" "}
                {estatisticasNaoExecucao.totalNaoExecutados}
              </Typography>
              <Typography variant="body2">
                <strong>Total de agendamentos:</strong>{" "}
                {estatisticasNaoExecucao.totalAgendamentos}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color:
                    estatisticasNaoExecucao.percentagemNaoExecutadosTotal > 25
                      ? "#e65a00"
                      : "inherit",
                }}
              >
                Taxa de não execução:{" "}
                {estatisticasNaoExecucao.percentagemNaoExecutadosTotal}%
              </Typography>
            </div>

            <Divider sx={{ my: 1 }} />

            {/* Motivos de não execução */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", mt: 1, mb: 1 }}
            >
              Motivos de Não Execução
            </Typography>

            {estatisticasNaoExecucao.tiposNaoExecucao.map(
              (tipoNaoExecucao, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: "12px",
                    paddingBottom:
                      index !==
                      estatisticasNaoExecucao.tiposNaoExecucao.length - 1
                        ? "8px"
                        : 0,
                    borderBottom:
                      index !==
                      estatisticasNaoExecucao.tiposNaoExecucao.length - 1
                        ? "1px dashed #eee"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <Chip
                      label={tipoNaoExecucao.motivo}
                      size="small"
                      style={{
                        backgroundColor:
                          MOTIVO_COLORS[tipoNaoExecucao.codigo] || "#e0e0e0",
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {tipoNaoExecucao.quantidade} (
                      {tipoNaoExecucao.percentagem}%)
                    </Typography>
                  </div>

                  {/* Exemplos recentes deste tipo */}
                  {tipoNaoExecucao.detalhes &&
                    tipoNaoExecucao.detalhes.length > 0 &&
                    tipoNaoExecucao.detalhes.slice(0, 2).map((detalhe, idx) => (
                      <div
                        key={idx}
                        style={{
                          marginLeft: "8px",
                          fontSize: "11px",
                          marginTop: "4px",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#666" }}>
                          • {formatarData(detalhe.data)} -{" "}
                          {detalhe.procedimento}
                          {detalhe.detalhe && (
                            <span style={{ fontStyle: "italic" }}>
                              {" "}
                              - "{detalhe.detalhe.substring(0, 40)}
                              {detalhe.detalhe.length > 40 ? "..." : ""}"
                            </span>
                          )}
                        </Typography>
                      </div>
                    ))}

                  {tipoNaoExecucao.detalhes &&
                    tipoNaoExecucao.detalhes.length > 2 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#666",
                          marginLeft: "8px",
                          marginTop: "2px",
                        }}
                      >
                        • E mais {tipoNaoExecucao.detalhes.length - 2}{" "}
                        ocorrência(s)...
                      </Typography>
                    )}
                </div>
              )
            )}
          </Paper>
        }
      >
        <InfoIcon
          fontSize="small"
          color={
            estatisticasNaoExecucao.totalNaoExecutados === 0
              ? "success"
              : estatisticasNaoExecucao.percentagemNaoExecutadosTotal < 10
              ? "warning"
              : "error"
          }
          sx={{
            ml: 1,
            cursor: "pointer",
            "&:hover": {
              color:
                estatisticasNaoExecucao.totalNaoExecutados === 0
                  ? "green"
                  : estatisticasNaoExecucao.percentagemNaoExecutadosTotal < 10
                  ? "#e65a00"
                  : "crimson",
            },
          }}
        />
      </HtmlTooltip>
    </span>
  );
};

export default NaoExecucaoInfoTooltip;
